import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { castDraft } from 'immer';

import { FilterTagsCondition } from 'dashboard/components/TagsConditionFilter';
import { DEFAULT_PAGE_SIZE } from 'dashboard/lib/constants/pagination';
import { SortParams } from 'dashboard/lib/interfaces/SortParams';
import { Account } from 'dashboard/models/account';
import { Environment } from 'dashboard/models/environment';
import { PaginationParams } from 'lib/interfaces/api';
import { Iso8601DateString } from 'lib/types';

export interface AccountsFilterParams {
  accountStatusIds?: string[];
  accountTypeIds?: string[];
  omni?: string;
  tagIds?: string[];
  tagsConditions?: FilterTagsCondition[][];
  updatedAtEnd?: Iso8601DateString;
  updatedAtStart?: Iso8601DateString;
  listIds?: string[];
}

interface AccountsPayload {
  accounts: Account[];
  environment: Environment;
  paginationParams: PaginationParams;
}

export interface AccountsState {
  accounts: Record<string, Account[]>;
  filters: AccountsFilterParams;
  sortParams: SortParams;
  paginationParams: PaginationParams | undefined;
  pageSize: number;
  currentPage: number;
}

const initialState: AccountsState = {
  accounts: {},
  filters: {},
  sortParams: { sort: 'updated_at', shouldSortDescending: true },
  paginationParams: undefined,
  pageSize: DEFAULT_PAGE_SIZE,
  currentPage: 1,
};

const accountsSlice = createSlice({
  name: 'accounts',
  initialState,
  reducers: {
    saveAccounts: (state, action: PayloadAction<AccountsPayload>) => {
      const { accounts, environment, paginationParams } = action.payload;
      state.accounts[environment.id] = castDraft(accounts);
      state.paginationParams = paginationParams;
    },
    saveCurrentPage: (state, action: PayloadAction<number>) => {
      state.currentPage = action.payload;
    },
    savePageSize: (state, action: PayloadAction<number>) => {
      state.pageSize = action.payload;
    },
    saveAccountsFilters: (state, action: PayloadAction<AccountsFilterParams>) => {
      state.filters = { ...state.filters, ...action.payload };
    },
    saveAccountsSortParams: (state, action: PayloadAction<SortParams>) => {
      state.sortParams = action.payload;
    },
  },
});

export const {
  saveAccounts,
  saveCurrentPage,
  savePageSize,
  saveAccountsFilters,
  saveAccountsSortParams,
} = accountsSlice.actions;

export const accountsReducer = accountsSlice.reducer;
